import type { Item } from '@shared/types-v0.1.0'
import { merge } from './merge'

export type HistoryItem = {
	timestamp: number
	item: Item
	metaData?: any
}

interface IdStructure {
	id: string
	tb: string
}

export const findObjectsWithValidId = (data: any): any[] => {
	const result: any[] = []

	if (typeof data !== 'object' || data === null) {
		return result
	}

	if (hasValidIdField(data)) {
		result.push(data)
	}

	if (Array.isArray(data)) {
		for (const item of data) {
			if (item?.id?.tb !== 'history') result.push(...findObjectsWithValidId(item))
		}
	} else {
		for (const value of Object.values(data)) {
			result.push(...findObjectsWithValidId(value))
		}
	}

	return result
}

export const isValidIdStructure = (obj: any): obj is IdStructure => {
	return typeof obj === 'object' && obj !== null && typeof obj.id === 'string' && typeof obj.tb === 'string'
}

export const hasValidIdField = (obj: any): boolean => {
	return typeof obj === 'object' && obj !== null && 'id' in obj && isValidIdStructure(obj.id)
}

export const setRitems = (rawItems: ({ id: { id: string; tb: string } } & Record<string, unknown>)[], cleanTbAtRefs = false) => {
	return rawItems.map((rawItem) => {
		// Хак - иначе в историю попадают прокси и она начинает в первой аписи показывать не историю, а сам item.
		const item = R.libs.just.clone(getItem(rawItem, cleanTbAtRefs))
		const proxyItem = R.items[item.id]

		if (proxyItem) merge({ object: item, proxyObject: proxyItem })
		else R.items[item.id] = item

		return R.items[item.id]
	})
}

const getItem = (rawItem: { id: { id: string; tb: string } } & Record<string, unknown>, cleanTbAtRefs = false) => {
	const { id, ...item } = rawItem

	item.rid = id.toString()
	item.id = id.id
	item.table = id.tb
	// Обратная совместимость
	item.dbClass = id.tb.replaceAll('_', '-')

	// Очистка tb из refs если cleanTbAtRefs = true
	if (cleanTbAtRefs) {
		cleanTbFromRefs(item)
	}

	const prototype = Object.create(
		{
			getRef: (dbClass: string) => {
				const globalItem = R.items[id.id]
				if (globalItem?.[dbClass]) {
					if (Array.isArray(globalItem[dbClass])) return globalItem[dbClass].map((i) => R.items[i.id]).filter((i) => !!i)
					return R.items[globalItem[dbClass].id]
				}
				return undefined
			},
			getBackRef: (dbClass: string) => {
				let resultRefItem: Item | undefined
				R.libs.just.map(R.items as any, (_, refItem: any) => {
					if (refItem.dbClass === dbClass && refItem[item.dbClass as string]?.id === id.id) resultRefItem = refItem
				})
				return resultRefItem
			},
			getHistory: (count?: number) => {
				const snapshot = R.libs.valtio.snapshot
				const take = R.libs.remeda.take

				// Обратная совместимость
				// const oldHistoryItems = R.itemsHistory[id.id] ? (snapshot(R.itemsHistory[id.id]) as HistoryItem[]) : []

				const historyItems = Object.values(R.items)
					.filter((i: any) => i.table === 'history' && i.id && i.item.id.id === id.id)
					.map((i: any) => {
						const historyItem: any = {
							id: i.id,
							rid: i.rid,
							timestamp: i.timestamp,
							item: snapshot(i.item as any),
						}

						if (i.metaData) historyItem.metaData = snapshot(i.metaData)
						return historyItem
					})

				// const allHistoryItems = oldHistoryItems.concat(historyItems)

				const sortedHistoryItems = historyItems.sort((a, b) => b.timestamp - a.timestamp)

				return (count ? take(sortedHistoryItems, count) : sortedHistoryItems) as HistoryItem[]
			},
		},
		{
			id: { enumerable: true, writable: false, configurable: false, value: id.id },
			dbClass: { enumerable: true, writable: false, configurable: false, value: item.dbClass },
		}
	)

	return Object.assign(prototype, R.libs.just.omit(item, ['id', 'dbClass']))
}

const cleanTbFromRefs = (item: Record<string, unknown>): void => {
	const standardKeys = ['content', 'states', 'id', 'rid', 'table', 'dbClass']

	for (const key of Object.keys(item)) {
		if (!standardKeys.includes(key)) {
			const value = item[key]

			// Если это объект с id (ссылка)
			if (typeof value === 'object' && value !== null && 'id' in value && 'tb' in value) {
				const { tb, ...cleanRef } = value
				item[key] = cleanRef
			}

			// Если это массив ссылок
			if (Array.isArray(value)) {
				item[key] = value.map((refItem) => {
					if (typeof refItem === 'object' && refItem !== null && 'id' in refItem && 'tb' in refItem) {
						const { tb, ...cleanRef } = refItem
						return cleanRef
					}
					return refItem
				})
			}
		}
	}
}
